import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useTrophyStore } from '@/state/useTrophyStore'
import { useTenant } from '@/tenant/TenantProvider'
import { useSettingsStore } from '@/state/useSettingsStore'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { nearestOverall } from '@/milestones/progress'
import { describeMilestone } from '@/milestones/labels'
import { formatPercent } from '@/lib/format'
import type { Category } from '@/milestones/types'

interface Props {
  compact?: boolean
}

const CATEGORY_TONE: Record<Category, string> = {
  growth: 'from-[color:var(--brand-primary)] to-[color:var(--brand-primary)]',
  revenue: 'from-[color:var(--signal-good)] to-[color:var(--brand-primary)]',
  reliability: 'from-[color:var(--brand-secondary)] to-[color:var(--brand-primary)]',
  speed: 'from-[color:var(--brand-accent)] to-[color:var(--signal-warn)]',
}

export function MilestoneRail({ compact = false }: Props) {
  const { t } = useTranslation()
  const tenant = useTenant()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const unlocked = useTrophyStore((s) => s.unlocked)
  const locale = useSettingsStore((s) => s.locale)
  const defs = useMemo(
    () =>
      (tenant.milestones.defaultsEnabled ? DEFAULT_MILESTONES : []).concat(
        tenant.milestones.customDefinitions ?? [],
      ),
    [tenant],
  )

  const items = useMemo(() => {
    if (!snapshot) return []
    const set = new Set(unlocked.map((u) => u.milestoneId))
    // Jen skutecne nejblizsi cile — vic se na obrazovku nevejde.
    return nearestOverall(defs, snapshot, set, compact ? 2 : 3)
  }, [defs, snapshot, unlocked, compact])

  return (
    <section
      className={clsx(
        'flex flex-col rounded-3xl border border-border bg-surface-1/70 p-4',
        compact ? 'gap-2' : 'gap-3',
      )}
    >
      <header className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.18em] text-ink-subtle">
          {t('milestones.title')}
        </h3>
      </header>
      <ul className={clsx('grid gap-2', compact ? 'gap-1.5' : 'gap-3')}>
        {items.map((p) => {
          const { title, goalLabel } = describeMilestone(p.def, locale)
          const pulse = p.pct >= 0.9
          return (
            <li key={p.def.id} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className={clsx('truncate text-sm font-medium text-ink')}>{title}</span>
                <span className="font-mono text-xs text-ink-muted">{goalLabel}</span>
              </div>
              <div className="relative h-1.5 overflow-hidden rounded-full bg-surface-3/60">
                <motion.div
                  className={clsx(
                    'h-full rounded-full bg-gradient-to-r',
                    CATEGORY_TONE[p.def.category],
                  )}
                  initial={{ width: '0%' }}
                  animate={{ width: `${Math.round(p.pct * 100)}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
                {pulse && (
                  <span className="pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                )}
              </div>
              <div className="flex items-center justify-between text-[11px] text-ink-subtle">
                <span>{t(`milestones.category.${p.def.category}`)}</span>
                <span>{formatPercent(p.pct * 100, locale, 0)}</span>
              </div>
            </li>
          )
        })}
        {items.length === 0 && (
          <li className="rounded-xl bg-surface-2/60 p-3 text-center text-xs text-ink-subtle">
            Všechno odemčené! 🎉
          </li>
        )}
      </ul>
    </section>
  )
}
